import { Text, UnstyledButton } from "@marketplace/ui";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

export interface ProductGuideSection {
  id: string;
  label: string;
  description?: string;
}

interface ProductGuideProps {
  activeSection?: string;
  onSectionClick?: (sectionId: string) => void;
  productCategory?: "software" | "api" | "connectivity";
}

export const ProductGuide: React.FC<ProductGuideProps> = ({
  activeSection,
  onSectionClick,
  productCategory = "software"
}) => {
  const { t: tProduct } = useTranslation("product");

  const sections: ProductGuideSection[] = [
    {
      id: "product-detail",
      label: tProduct("guide.sections.productDetail.title"),
      description: tProduct("guide.sections.productDetail.description")
    },
    {
      id: "media",
      label: tProduct("guide.sections.media.title"),
      description: tProduct("guide.sections.media.description")
    },
    {
      id: "recurring-order",
      label: tProduct("guide.sections.recurringOrder.title"),
      description: tProduct("guide.sections.recurringOrder.description")
    },
    {
      id: "product-packages",
      label: tProduct("guide.sections.productPackages.title"),
      description: tProduct("guide.sections.productPackages.description")
    },
    {
      id: "terms-condition",
      label: tProduct("guide.sections.termsCondition.title"),
      description: tProduct("guide.sections.termsCondition.description")
    }
  ];

  // Add API Package section if productCategory is api
  if (productCategory === "api") {
    sections.push({
      id: "api-package",
      label: tProduct("guide.sections.apiPackage.title"),
      description: tProduct("guide.sections.apiPackage.description")
    });
  }

  const handleSectionClick = (sectionId: string) => {
    if (onSectionClick) {
      onSectionClick(sectionId);
    }

    // Scroll to section
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="space-y-6 w-[290px] sticky top-1 h-fit">
      <div className="bg-brand-white rounded-lg border border-solid border-mischka-400">
        <div className="p-6 border-solid border-0 border-b border-b-mischka-400">
          <Text
            type="heading"
            size="l"
            weight="bold"
            className="text-fjord-grey-400"
          >
            {tProduct("guide.title")}
          </Text>
        </div>
        <div className="p-6">
          <div className="flex flex-col">
            {sections.map((section) => (
              <UnstyledButton
                key={section.id}
                type="button"
                onClick={() => handleSectionClick(section.id)}
                className={clsx(
                  "group text-left transition-colors cursor-pointer hover:bg-[#F1F9FF] px-4 py-[10px] rounded-lg",
                  activeSection === section.id
                    ? "bg-[#F1F9FF]"
                    : "bg-transparent"
                )}
              >
                <Text
                  type="heading"
                  size="xxs"
                  weight="semibold"
                  className={clsx(
                    "group-hover:text-fjord-grey-500",
                    activeSection === section.id
                      ? "text-fjord-grey-500"
                      : "text-fjord-grey-200"
                  )}
                >
                  {section.label}
                </Text>
                {section.description && (
                  <Text
                    size="xs"
                    className={clsx(
                      "group-hover:text-fjord-grey-400 mt-1",
                      activeSection === section.id
                        ? "text-fjord-grey-400"
                        : "text-fjord-grey-200"
                    )}
                  >
                    {section.description}
                  </Text>
                )}
              </UnstyledButton>
            ))}
          </div>
        </div>
      </div>
      <div className="bg-brand-white rounded-lg border border-solid border-mischka-400">
        <div className="p-6 space-y-2">
          <Text
            type="heading"
            size="xxs"
            weight="semibold"
            className="text-fjord-grey-500"
          >
            {tProduct("guide.legend")}
          </Text>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-x-2 text-fjord-grey-500">
              <Text component="span" size="xs">
                "<span className="text-[#FF3B30]">*</span>"
              </Text>
              <Text size="xs" className="text-fjord-grey-400">
                {tProduct("guide.mandatory")}
              </Text>
            </div>
            <div className="flex items-center gap-x-2 text-fjord-grey-500">
              <Text component="span" size="xs">
                <span className="inline-block mr-2">"</span>
                <span>"</span>
              </Text>
              <Text size="xs" className="text-fjord-grey-400">
                {tProduct("guide.optional")}
              </Text>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
